import * as utils from '@app/shared/utils/utils';

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {GenericMedia, IChatMessage} from '@app/models/chat';
import {ITicket} from '@app/shared/models/ticket';
import {Observable, Subject, Subscription, throwError} from 'rxjs';
import {catchError, filter, map, take, tap} from 'rxjs/operators';

import {AudioRecorderService} from '@app/services/audio-recorder.service';
import {ChatService} from '@app/services/chat.service';
import {environment} from '@environments/environment';
import {MaintenanceManagerService} from "../../../services/maintenance-manager.service";
import {SettingsManagerService} from "../../../services/settings-manager.service";
import {ResourceManager} from "../../../services/resource-manager";
import {Equipment} from "../../../models/equipment";
import {
  ChooseResourceDialogComponent,
  ChooseResourceDialogData
} from "../../../components/dialogs/choose-resource-dialog/choose-resource-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {Paginator} from "../../../models/paginator";
import {Assistance} from "../../../models/assistance";

@Component({
  selector: 'tlm-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatComponent implements OnInit, OnDestroy {

  @Input()
  equipment: Equipment;

  @Input()
  welcomeMessage: string;

  @Input()
  autosendVideocallRequest: boolean;

  /*@Input()
  singleVideocallRequest: boolean;*/

  @Input('user') set userData(data: any) {
    if (data) {
      this.user = data;
      this.userId = `${data.id}`;
    }
  }
  @Input('ticket') set newOperation(data: ITicket) {
    if (data) {
      this.operation = data;
    }
  }

  audioTime$: Observable<string> = this.audioRecorderService.recordedTime;
  pagination$: Observable<Array<IChatMessage>>;
  subscriptions: Array<Subscription> = [];
  startAt = new Subject();
  operation: ITicket;
  sending = false;
  userId: string;
  messages: Array<IChatMessage>;
  message: FormControl;
  fileInput: FormControl;
  user: any;
  dragOver = false;
  isRecording = false;
  paginationLoading = false;
  initStatus: boolean;

  constructor(
    private chatService: ChatService,
    private audioRecorderService: AudioRecorderService,
    private maintenanceManager: MaintenanceManagerService,
    private settingsManagerService: SettingsManagerService,
    private resourceManager: ResourceManager,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef) { }

  ngOnInit() {

    // Bind send card function
    this.maintenanceManager.sendVideoCallCard = (assistance: Assistance, operatorId: string, pin: string) => this.sendVideocallCard(assistance, operatorId, pin);
    this.maintenanceManager.closeChat = (assistance: Assistance) => this.closeChat(assistance);

    this.initStatus = true;
    this.message = new FormControl('', [Validators.required]);
    this.fileInput = new FormControl('');
    const sub = this.audioRecorderService.recordedBlob.pipe(
      filter((data) => data !== null),
      tap((data) => this.saveAudio(data))
    ).subscribe();
    this.subscriptions.push(sub);
    this.initChat();

    if (this.autosendVideocallRequest) {
      this.requestCall();
    }
  }

  /** Add chat if not exists and reset messages */
  initChat() {
    this.initStatus = true;
    this.chatService.joinChat(this.operation, this.user).pipe(
      take(1),
      tap(() => {
        this.getMessages();
        if (this.operation.status != 2) {
          this.sendMessage(this.welcomeMessage);
        }
      })
    ).subscribe();
  }

  /** Manage message fetching and pagination */
  async getMessages() {
    await this.getRealTimeMessages();
    this.cd.detectChanges();
  }

  /**
   * Listen pagination changes if initStatus is true
   * otherwise fech first page
   */
  async getRealTimeMessages() {
    /*const pagination$ = this.startAt.pipe(
      filter(() => !this.initStatus),
      tap(() => this.paginationLoading = true),
      switchMap(() => this.chatService.getPaginationMessage().pipe(
        takeWhile((messages: Array<IChatMessage>) => this.checkEndOfPagination(messages[0].sentAt)),
        tap((messages: Array<IChatMessage>) => {
          this.chatService.setLastKeyValue(messages);
          this.scrollBottom(true);
        })
      )),
      tap(() => this.paginationLoading = false),
    );*/

    // Init messages if not defined
    this.messages = this.messages || [];

    // Fetching and filtering messages already present in chat
    const fetching$ = (await this.chatService.getChatMessages()).pipe(
      map(messages => messages.filter(message => !this.messages.map(_message => _message.messageID).includes(message.messageID))),
    );

    // merge(pagination$, fetching$).subscribe((newMessages: IChatMessage[]) => {
    fetching$.subscribe((newMessages: IChatMessage[]) => {
      this.messages = this.messages.concat(newMessages);
      this.initStatus = false;
      this.cd.detectChanges();
      this.scrollBottom();
    });
  }

  /**
   * Set pagination Observable complete condition
   * @param key sentAt value of messages
   */
  checkEndOfPagination(key: number): boolean {
    const check = this.chatService.checkLastKeyValue(key);
    if (!check) {
      this.paginationLoading = false;
      this.cd.detectChanges();
    }

    return check;
  }

  /**
   * Send text message
   */
  onSend() {
    if (this.message.valid) {
      this.setSendingStatus();
      this.sendMessage(this.message.value);
    }
  }

  /**
   * Add new message on Firebase
   * @param text text of message
   * @param media media object
   */
  sendMessage(text: string, media?: any) {
    this.chatService.sendMessage(this.userId, text, media).then(() => {
      this.message.reset();
      this.sending = false;
      this.cd.detectChanges();
    });
  }

  /** scroll the chat container to the end or custom position */
  scrollBottom(top?: boolean) {
    try {
      const div = document.getElementById('chatBox');
      div.scrollTop = (top) ? 30 : div.scrollHeight;
    } catch (e) {}
  }

  /** Handle chat container scrolling for pagination */
  scrollHandler(event: any) {
    if (event === 'top' && !this.initStatus) {
      this.startAt.next();
    }
  }

  /** Detech drag event and set relative status */
  dragHover() {
    this.dragOver = true;
    this.cd.detectChanges();
  }

  /** Detech drag leave event and set relative status */
  dragLeave() {
    this.dragOver = false;
    this.cd.detectChanges();
  }

  /** Detech drop event and start file uploading */
  drop(files: FileList) {
    const filename = environment.storage.chatFilePath.replace('{0}', `ticket_${this.operation.id}`) + files[0].name;
    this.uploadFile(files, filename);
  }

  /** Upload file on FireStorage */
  async uploadFile(files: FileList, filename: string) {
    const maxUploadSize = await this.settingsManagerService.getFileMaxUploadSize();
    if (files[0].size > 1024 * 1024 * maxUploadSize) {
      alert(`Dimensione massima supportata: ${maxUploadSize}mb`);
      return;
    }
    this.setSendingStatus();
    const metadata = await this.chatService.uploadChatAttachment(files[0]);
    const media = new GenericMedia(metadata);
    this.sendMessage('', media);
    this.fileInput.reset();
    document.getElementById('fileInput')['value'] = '';
  }

  /** Start audio recording */
  startRecording() {
    if (!this.isRecording) {
      this.isRecording = true;
      this.audioRecorderService.startRecording();
    }
  }

  /** Undo audio recording */
  abortRecording() {
    if (this.isRecording) {
      this.isRecording = false;
      this.audioRecorderService.abortRecording();
    }
  }

  /** Stop and save audio recording */
  stopRecording() {
    if (this.isRecording) {
      this.audioRecorderService.stopRecording();
      this.isRecording = false;
    }
  }

  /** Save audio recording and send message */
  saveAudio(blob) {
    if (!blob) {
      return;
    }
    this.setSendingStatus();
    this.chatService.saveAudio(blob, this.operation).pipe(
      take(1),
      tap((metadata) => {
        const media = new GenericMedia(metadata);
        this.sendMessage('', media);
        this.audioRecorderService.clearStream();
      }),
      catchError((error) => throwError(error))
    ).subscribe();
  }


  /** Set new message loading status */
  private setSendingStatus() {
    this.sending = true;
    this.cd.detectChanges();
    this.scrollBottom();
  }

  /** Set a message as delivered */
  setDeliveryMessage(event: any) {
    this.chatService.setMessageDelivered(event, `${this.user.id}`);
  }

  /** Prepare video call request */
  requestCall() {

    // if (!this.singleVideocallRequest || (this.singleVideocallRequest && !this.maintenanceManager.isVideocallRequestAlreadySent(this.operation.id))) {
    this.maintenanceManager.openVideoCall();

    /*const params = {
      roomId: this.chatService.generateRoomName(),
      // userId: this.patient.firebaseUid,
      callerName: `${this.user.firstName} ${this.user.lastName}`
    };

    const json = {
      roomName : this.chatService.generateRoomName(),
      userTurns : true,
      startAsServer : true
    };

    // avvia la videochiamata
    this.openCall(json);*/
  }

  sendVideocallCard(assistance: Assistance, operatorId: string, pin: string) {
    this.chatService.sendVideocallRequest(assistance, operatorId, pin);
  }

  closeChat(assistance: Assistance) {
    this.chatService.closeChat(assistance);
  }

  async sendResource() {

    const dialogRef = this.dialog.open(ChooseResourceDialogComponent, {
      width: '1200px',
      data: { equipment: this.equipment } as ChooseResourceDialogData,
    });
    const result: ChooseResourceDialogData = await dialogRef.afterClosed().toPromise();

    if (result) {

      // Get video duration if resource is video
      const metadata = await this.resourceManager.getResourceMetadata(result.resource, new Paginator({ ignorePagination: true }));

      const media = new GenericMedia({
        id: result.resource.id,
        name: result.resource.name,
        size: metadata.size || 0,
        contentType: metadata.contentType,
        duration: metadata.duration,
      });

      this.sendMessage('', media);

    }

  }

  sendProcedure() {
    // TODO
  }

  /** Open video call module */
  openCall(params: any) {
    window.open('../../../../../external/videcall/index.html?' + utils.objToQueryString(params));
  }

  /** Destroy all subscriptions */
  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((sub) => sub.unsubscribe() );
    }
  }
}
